<template>
    <drag-and-drop-endpoints
        :js-plumb-instance="jsPlumbInstance"

        :reference-parameters="referenceParameters"
        :endpoint-parameters="endpoints"

        @endpoints-updated="endpointsUpdated"
        @endpoint-created="endpointCreated"

        @click="eventProxies.click"
        @dblclick="eventProxies.dblclick"
        @contextmenu="eventProxies.contextmenu"
        @mouseover="eventProxies.mouseover"
        @mouseout="onMouseOut"
        @mousedown="eventProxies.mousedown"
        @mouseup="eventProxies.mouseup"
        @maxConnections="eventProxies.maxConnections"
    >
        <slot />
    </drag-and-drop-endpoints>
</template>

<script>
    import $ from 'jquery';
    
    import {createNamespacedHelpers} from 'vuex';
    
    import {COLORS} from "@modules/JourneyBuilder/constants";
    import {brickDefinitions} from '@modules/JourneyBuilder/Bricks/brick-definitions';
    import ENDPOINT_TYPES from '@core/Components/DragAndDrop/endpoint-types';

    import journeyBrickEndpointUuidMixin from '@modules/JourneyBuilder/Mixins/journey-brick-endpoint-uuid';
    import journeyBrickEndpointsMixin from '@modules/JourneyBuilder/Mixins/journey-brick-endpoints';
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';

    import DragAndDropEndpoints from "@core/Components/DragAndDrop/DragAndDropEndpoints.vue";
    
    const {mapState, mapGetters} = createNamespacedHelpers('journeyBuilder/composer');
    
    const JS_PLUMB_PAINTSTYLE = 'paintStyle';
    const JS_PLUMB_PARAMETERS = 'parameters';
    
    const ENDPOINT_LOCATIONS = {
        LEFT: 'left',
        RIGHT: 'right',
        BOTTOM: 'bottom',
        TOP: 'top'
    };
    
    /**
     * Get the position of the given endpoint relative to the given component
     * @param {HTMLElement} endpoint.element Component endpoint is connected to
     * @param {HTMLElement} endpoint.canvas Endpoint canvas
     * @return {string} Location
     */
    const getEndpointLocation = (endpoint) => {
        const {
            top: componentTop,
            bottom: componentBottom,
            left: componentLeft
        } = endpoint.element.getBoundingClientRect();

        const {
            top: endpointTop,
            bottom: endpointBottom,
            left: endpointLeft
        } = endpoint.canvas.getBoundingClientRect();

        //Both positions for bottom, and right are off by a ~1-2 pixels
        //to combat this; we'll default to 'right', and apply a small margin
        //of error to 'bottom'

        //Endpoint is above our component
        if(componentTop > endpointTop) { return ENDPOINT_LOCATIONS.TOP; }

        //Endpoint is on the bottom of our component
        if(endpointBottom > componentBottom - 5) { return ENDPOINT_LOCATIONS.BOTTOM; }

        //Endpoint is on the left of our component
        if(componentLeft > endpointLeft){ return ENDPOINT_LOCATIONS.LEFT; }

        //Endpoint is on the right of our component
        return ENDPOINT_LOCATIONS.RIGHT;
    };
    
    export default {
        name: 'brick-square-endpoints',
        components: {
            DragAndDropEndpoints
        },
        mixins: [journeyBrickEndpointUuidMixin, journeyBrickEndpointsMixin, permissionsMixin],
        created() {
            const vm = this;
            vm.referenceParameters.endpoint = [ENDPOINT_TYPES.DOT, {}];
            
            //Our styling needs to adapt to our brick menu being shown
            //this can be done via a watcher assigning/removing a class depending on the state of our brick menu
            this.$watch(
                () => this.menuShown(this.brickId),
                menuShown => {
                    const handler = menuShown ?
                        endpoint => endpoint.canvas.classList.add('brick-menu-dropdown-shown') :
                        endpoint => endpoint.canvas.classList.remove('brick-menu-dropdown-shown');

                    this.forAllEndpoints(handler);
                }
            );

            const readFinishedInitialRenderCycle = () => {
                return this.$store.state.journeyBuilder.composer.finishedInitialRenderCycle;
            }
            
            //Once we have finished our initial render cycle, we should update our anchor locations again
            //depending on timing (state of the DOM at time of invocation), some anchors may face an incorrect direction
            if(!readFinishedInitialRenderCycle()){
                const unWatch = this.$watch(
                    readFinishedInitialRenderCycle,
                    () => {
                        unWatch();
                        
                        //Schedule our update at the earliest possible time
                        //allow Vue to continue its render cycle
                        setTimeout(this.updateAllEndpointAnchorLocations, 0);
                    }
                )
            }
        },
        mounted() {
            this.$el.addEventListener('mouseover', () => {
                this.forAllEndpoints(e => e.canvas.classList.add('brick-hover'))
            });
            
            this.$el.addEventListener('mouseout', () => {
                this.forAllEndpoints(e => e.canvas.classList.remove('brick-hover'))
            });
        },
        methods: {
            endpointsUpdated(){
                this.applyEndpointCustomization();
            },
            onMouseOut(endpoint){
                //In some rare cases our tooltip might start lingering
                //thus we need to forcefully hide it to prevent it from becoming a zombie
                if(endpoint.getParameter('hasTooltip')){
                    $(endpoint.canvas).popover('hide');
                }
                
                this.eventProxies.mouseout(...arguments);
            },
            endpointCreated(jsPlumbEndpoint){
                //setCurrentFace is invoked every time our anchor moves
                const _setCurrentFace = jsPlumbEndpoint.anchor.setCurrentFace;

                const vm = this;

                //Canvas only needs to be setup once
                const canvas = jsPlumbEndpoint.canvas;
                const cssClass = jsPlumbEndpoint.getParameter('cssClass');

                canvas.classList.add('jc-endpoint');
                if(jsPlumbEndpoint.isTarget) canvas.classList.add('jc-incoming');
                if(jsPlumbEndpoint.isSource) canvas.classList.add('jc-outgoing');

                //Workaround for jsPlumb breaking when adding classes
                //classList does not like spaces/can't handle arrays 
                //thus we'll have to spread the individual classes ourselves
                if(cssClass) canvas.classList.add(...cssClass.split(' '));

                //Ensure our newly created endpoint is inserted directly after the element it is attached to in the DOM
                //Further repositioning will be done on element drag start to ensure correct positioning 
                vm.$el.parentElement.insertBefore(
                    canvas,
                    vm.$el.nextElementSibling
                );

                jsPlumbEndpoint.anchor.setCurrentFace = function(){
                    _setCurrentFace.apply(this, arguments);

                    //Update our location post update to ensure the correct location is used
                    vm.updateEndpointAnchorLocation(jsPlumbEndpoint);
                };

                //An indirect invocation after creation is not guaranteed
                vm.updateEndpointAnchorLocation(jsPlumbEndpoint);
            },
            updateEndpointAnchorLocation(endpoint){
                //Protect against deleted/not yet rendered endpoints
                if(!endpoint && endpoint.canvas){
                    return;
                }

                setTimeout(() => endpoint.canvas.setAttribute('data-location', getEndpointLocation(endpoint)));
            },
            forAllEndpoints(callback){
                this.endpoints.forEach(({uuid}) => {
                    callback(
                        this.jsPlumbInstance.getEndpoint(uuid)
                    );
                });
            },
            updateAllEndpointAnchorLocations(){
                this.forAllEndpoints(
                    endpoint => this.updateEndpointAnchorLocation(endpoint)
                );
            },
            applyEndpointCustomization(){
                const vm = this;
                
                const jsPlumbInstance = vm.jsPlumbInstance;

                jsPlumbInstance.batch(() => {
                    vm.endpoints.forEach(async endpoint => {
                        if(!endpoint.isSource){
                            return;
                        }
                        
                        const uuid = endpoint.uuid;
                        const { 
                            title: tooltipTitle = '',
                            content: tooltipContentAwaitable = ''
                        } = endpoint.parameters.tooltip;
                        const { strokeWidth, stroke } = endpoint.paintStyle;
                        
                        const jsPlumbEndpoint = jsPlumbInstance.getEndpoint(uuid);

                        //Our endpoint might've been deleted and thus not exist anymore
                        if(!jsPlumbEndpoint){
                            return;
                        }
                        
                        const canvas = jsPlumbEndpoint.canvas;
                        
                        //Keep track of which endpoints have tooltips
                        //'old' tooltips should always be removed
                        const hadTooltip = jsPlumbEndpoint.getParameter('hasTooltip');
                        jsPlumbEndpoint.setParameter('hasTooltip', endpoint.parameters.hasTooltip);
                        
                        if(hadTooltip){
                            const $canvas = $(canvas);
                            
                            //It does not seem possible to update popover content once the popover has been created
                            //apart from that, the 'destroy' function mentioned in the docs does not seem to exist
                            //thus the best we can do is remove from our data collection, and re-create the popover
                            $canvas.removeData('bs.popover');
                            
                            if(endpoint.parameters.hasTooltip){
                                //tooltipContentAwaitable can be either a string, or a promise
                                //both are awaitable, non-'thennable' objects simply resolve immediately
                                // noinspection ES6RedundantAwait
                                const content = await tooltipContentAwaitable;

                                //We can't make any assumptions about the time it takes for our function to complete
                                //attempting to remove the popover once again should be our safest bet
                                //regarding changes that might've taken place while our current promise was still computing
                                $canvas.removeData('bs.popover');

                                $canvas.popover({
                                    title: tooltipTitle,
                                    content: content,
                                    trigger: 'hover',
                                    html: true
                                });
                            }
                        }
                        
                        if(stroke || strokeWidth){
                            canvas.classList.add('jc-custom-stroke');
                            
                            //Re-use our old style; we only want to re-color
                            const paintStyle = jsPlumbEndpoint.getPaintStyle();
                            paintStyle.strokeWidth = strokeWidth;
                            paintStyle.stroke = stroke;
                            jsPlumbEndpoint.setPaintStyle(paintStyle);   
                        }
                    });
                });
                
                this.updateAllEndpointAnchorLocations();
            },
            createConnectionFromDrop(targetElement, sourceEndpoint){
                // When an endpoint is dropped on a connection accepting journey brick
                // connect our source brick with the target brick at the location of the drop event

                //Our targetElement may be an element contained within a brick; closest returns self/the first ancestor matching the selector
                const targetBrick = targetElement.closest('.brick');
                
                if(!targetBrick){
                    return;
                }
                
                //Our target endpoints accept a single connection each
                //our circle bricks will never have empty target connection collections
                const targetEndpoint = this.jsPlumbInstance
                    .getEndpoints(targetBrick)
                    .find(e => e.isTarget && e.connections.length === 0);

                if(!targetEndpoint){
                    return;
                }

                const { fromToId: sourceBrickId, connectionId } = this.deconstructEndpointUuid(sourceEndpoint.getUuid());
                const { fromToId: destinationBrickId } = this.deconstructEndpointUuid(targetEndpoint.getUuid());

                //When a connection is dropped on it's originating brick, our handler will still be invoked
                //back out to prevent connecting a brick to itself
                if(sourceBrickId === destinationBrickId){
                    return;
                }
                
                this.$emit('connection-created', {
                    connectionId: connectionId,
                    sourceBrickId: sourceBrickId,
                    destinationBrickId: destinationBrickId
                });
            }
        },
        watch: {
            endpoints(newEndpoints, oldEndpoints){
                //This should trigger an event on DragAndDropEndpoints once the new endpoints are processed
                if(newEndpoints.length !== oldEndpoints.length){
                    return;
                }

                const endpointsRequireCustomization = newEndpoints.some(newEndpoint => {
                    const oldEndpoint = oldEndpoints.find(oldEndpoint => oldEndpoint.uuid === newEndpoint.uuid);
                    
                    //This should get picked up and trigger an event by our underlying DragAndDropEndpoints component
                    if(!oldEndpoint){
                        return false;
                    }
                    
                    const diffedProperties = [JS_PLUMB_PAINTSTYLE, JS_PLUMB_PARAMETERS];

                    return diffedProperties.some(property => {
                        const newProperty = newEndpoint[property];
                        const oldProperty = oldEndpoint[property];

                        //When either is falsey, check whether one of two was defined in our previous iteration
                        if(!oldProperty || !newProperty){
                            return newProperty !== oldProperty;
                        }

                        //For the time being we assume a static set of keys
                        //with simply differing values
                        const propertyKeys = Object.keys(newProperty);

                        return propertyKeys.some(key => newProperty[key] !== oldProperty[key]);
                    });
                });

                //Styling has changed, re-apply our customization
                if(endpointsRequireCustomization){
                    this.applyEndpointCustomization();
                }
            }
        },
        computed: {
            ...mapGetters(['menuShown']),
            ...mapState(['brickMap']),
            endpoints(){
                const vm = this;
                const brickDefinition = brickDefinitions[vm.brickMap[vm.brickId].type];
                
                const endpoints = [];

                const outgoingEndpoints = vm.outgoingConnections.map(connection => {
                    const endpointUuid = vm.getBrickEndpointUuid(connection.id, connection.sourceBrickId);
                    
                    return {
                        uuid: endpointUuid,
                        endpoint: ENDPOINT_TYPES.DOT,
                        //cssClass is bugged. Using it breaks the Drag and Drop functionality of the endpoints.
                        // cssClass: connection.ui.cssClass,

                        /*
                            Docs regarding endpoint paintStyle options do not match our jsPlumb version
                        
                            From Docs:
                            { 
                                fill:"blue", 
                                outlineStroke:"black", 
                                outlineWidth:1 
                            }
                        
                            From TS declaration:
                                               
                            interface PaintStyle {
                                stroke?: string;
                                fill?: string;
                                strokeWidth?: number;
                            }
                         */
                        [JS_PLUMB_PAINTSTYLE]: { 
                            fill: connection.ui.backgroundColor || COLORS.BRICK_ENDPOINT_OUTGOING,
                            strokeWidth: connection.ui.borderColor ? 1 : undefined,
                            stroke: connection.ui.borderColor || undefined
                        },
                        //Prevent dragging when in readonly mode
                        //Our JourneyComposerDragAndDropWrapper stalls rendering until our canWrite value is loaded
                        //thus we may always assume to have a valid canWrite value at this point
                        //invoking setEnabled()/setEnabled(true) seems to not re-enable dragging for our endpoints 
                        enabled: vm.canWrite,
                        isSource: true,
                        isTarget: false,
                        [JS_PLUMB_PARAMETERS]: {
                            tooltip: connection.ui.tooltip,
                            
                            //A tooltip object being present should be enough to trigger a tooltip
                            hasTooltip: !!connection.ui.tooltip,
                            
                            cssClass: connection.ui.cssClass
                        },
                        dragOptions: {
                            stop(info){
                                //Our endpoint is not part of the event parameters
                                const endpoint = vm.jsPlumbInstance.getEndpoint(endpointUuid);

                                //While anchor.setCurrentFace is invoked while dragging, it is not invoked upon dropping
                                //this causes our icon to potentially face the wrong way
                                //we can combat this by invoking updateEndpointAnchorLocation in our drag stop handler
                                vm.updateEndpointAnchorLocation(endpoint);
                                
                                vm.createConnectionFromDrop(info.e.target, endpoint);
                            }
                        }
                    };
                });

                endpoints.push(...outgoingEndpoints);

                if(vm.acceptsIncomingConnections){
                    const connectionOptions = brickDefinition.connectionOptions;
                  
                    const incomingEndpoints = vm.incomingConnections.map(connection => ({
                        uuid: vm.getBrickEndpointUuid(connection.id, connection.destinationBrickId),
                        paintStyle: { fill: COLORS.BRICK_ENDPOINT_INCOMING },
                        isTarget: true,
                        isSource: false
                    }));

                  //As long as we haven't hit our max, there should always be a single incoming endpoint to accept new connections
                  if(incomingEndpoints.length < connectionOptions.maxIncomingConnections){
                      incomingEndpoints.push({
                          uuid: vm.getBrickEndpointUuid(vm.brickId, vm.brickId),
                          connectorStyle: { stroke: COLORS.BRICK_CONNECTION_ENABLED, strokeWidth: 6 },
                          [JS_PLUMB_PAINTSTYLE]: { 
                              fill: COLORS.BRICK_ENDPOINT_INCOMING,

                              //Ensure same size as outgoing/connected incoming endpoints; outgoing endpoints have a strokeWidth parameter, which slightly increases their size
                              radius: 11
                          },
                          isTarget: true,
                          isSource: false,
                          endpoint: connectionOptions.unconnectedIncomingEndpointType,
    
                          //DropOptions are seemingly not documented
                          //Available callbacks can be found with some digging in node_modules/jsplumb/dist/js/jsplumb.js
                          //both over/out param objects seem to have the same structure
                          //the api does not seem to support an easy way of finding out whether an endpoint is being dragged
                          //over one of it's own siblings, thus those will also get marked upon 'over'
                          dropOptions: {
                              over: evt => {
                                evt.drop.el.classList.add('jc-hover');
                              },
                              out: evt => {
                                evt.drop.el.classList.remove('jc-hover');
                              }
                          }
                      })
                    }

                    endpoints.push(...incomingEndpoints);
                }
                return endpoints;
            }
        },
        data: () => ({
            referenceParameters: null
        }),
        props: {
            brickId: {
                type: String,
                required: true
            },
            incomingConnections: {
                type: Array,
                required: true
            },
            outgoingConnections: {
                type: Array,
                required: true
            },
            acceptsIncomingConnections : {
                type: Boolean,
                default: true
            }
        }
    }
</script>
